import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { ActivityStatus, ACTIVITY_STATUS_COLORS } from '../../../types/execution';
import { TimelineRange, parseDate, timelinePosition, formatDate, monthLabel } from './executionUtils';

interface GanttBarProps {
    range: TimelineRange;
    plannedStart: string | null;
    plannedEnd: string | null;
    progress: number;
    status: ActivityStatus;
    referenceDate: string;
    label?: string | null;
    bold?: boolean;
}

/** Barra de Gantt: faixa planejada + parte executada (proporcional ao % real) + linha da data de referência. */
export const GanttBar: React.FC<GanttBarProps> = ({
    range, plannedStart, plannedEnd, progress, status, referenceDate, label, bold,
}) => {
    const start = parseDate(plannedStart);
    const end = parseDate(plannedEnd);
    const ref = parseDate(referenceDate);
    const left = start ? timelinePosition(range, start) : 0;
    const right = end ? timelinePosition(range, end) : left;
    const width = Math.max(right - left, start && end ? 1 : 0);

    return (
        <Box sx={{ position: 'relative', height: bold ? 18 : 14, minWidth: 240 }}>
            {range.months.map((month) => (
                <Box
                    key={month.toISOString()}
                    sx={{
                        position: 'absolute', top: -6, bottom: -6, borderLeft: '1px dashed',
                        borderColor: 'divider', left: `${timelinePosition(range, month)}%`,
                    }}
                />
            ))}
            {start && end && (
                <Tooltip title={`${formatDate(plannedStart)} → ${formatDate(plannedEnd)} · ${Math.round(progress)}% real`}>
                    <Box
                        sx={{
                            position: 'absolute', top: 0, bottom: 0, left: `${left}%`, width: `${width}%`,
                            bgcolor: 'action.selected', borderRadius: 0.5, overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ height: '100%', width: `${Math.min(100, progress)}%`, bgcolor: ACTIVITY_STATUS_COLORS[status] }} />
                    </Box>
                </Tooltip>
            )}
            {label && (
                <Typography
                    variant="caption"
                    sx={{ position: 'absolute', left: `calc(${left + width}% + 4px)`, top: -2, fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                    {label}
                </Typography>
            )}
            {ref && (
                <Box sx={{ position: 'absolute', top: -6, bottom: -6, borderLeft: '2px solid', borderColor: 'error.main', left: `${timelinePosition(range, ref)}%` }} />
            )}
        </Box>
    );
};

export const GanttHeader: React.FC<{ range: TimelineRange }> = ({ range }) => (
    <Box sx={{ position: 'relative', height: 18, minWidth: 240 }}>
        {range.months.map((month) => (
            <Typography
                key={month.toISOString()}
                variant="caption"
                sx={{ position: 'absolute', left: `calc(${timelinePosition(range, month)}% + 2px)` }}
            >
                {monthLabel(month)}
            </Typography>
        ))}
    </Box>
);

export default GanttBar;
